import React, { useMemo, useState } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import CommonDataTable from "../../../../share-components/table/commonDataTable";

const orderHistorySelectStyles = {
    control: (base) => ({
        ...base,
        minHeight: 36,
        height: 36,
        fontSize: 12,
        borderRadius: 8,
        borderColor: "#e5e7eb",
    }),
    valueContainer: (base) => ({
        ...base,
        padding: "0 8px",
        height: 34,
    }),
    singleValue: (base) => ({
        ...base,
        fontSize: 12,
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: "4px 8px",
    }),
    menu: (base) => ({
        ...base,
        zIndex: 10,
        fontSize: 12,
    }),
};

const formatOrderNumber = (order) => {
    const raw = order.order_number ?? order.order_name ?? order.id;
    if (raw == null || raw === "") return "-";
    return String(raw).trim();
};

const getChannelIcon = (channel) => {
    if (!channel) return "fa-shopping-bag";
    const c = String(channel).toLowerCase();
    if (c.includes("website") || c.includes("web")) return "fa-globe";
    if (c.includes("mobile") || c.includes("app")) return "fa-mobile-alt";
    if (c.includes("store") || c.includes("in-store")) return "fa-store";
    return "fa-shopping-bag";
};

const getStatusPillVariant = (status) => {
    if (!status) return "default";
    const s = String(status).toLowerCase();
    if (s.includes("return")) return "returned";
    if (s.includes("deliver") || s.includes("complete")) return "delivered";
    return "default";
};

const BuyerLeadCommerce = ({
    totalOrders,
    totalOrderValue,
    averageOrderValue,
    lifetimeValue,
    returnRate,
    refundsTotal,
    roiPerUser,
    formatCurrency,
    orderHistory,
    formatDate,
    topProducts,
    categoryPreferences,
    abandonedCart,
    abandonedCartItems,
    formatDateTime,
    priceRangePreference,
    favoriteBrand,
    colorPreferences,
}) => {
    const rawOrders = orderHistory || [];
    const [orderSearch, setOrderSearch] = useState("");
    const [orderStatusFilter, setOrderStatusFilter] = useState("All Statuses");
    const [orderPage, setOrderPage] = useState(1);
    const [orderPerPage, setOrderPerPage] = useState(10);

    // Dynamically extract unique status values from backend orderHistory data
    const orderStatusOptions = useMemo(() => {
        const statuses = new Set();
        rawOrders.forEach((order) => {
            if (order.status != null && order.status !== "") {
                statuses.add(String(order.status));
            }
        });
        return ["All Statuses", ...Array.from(statuses).sort()];
    }, [rawOrders]);

    const filteredOrders = useMemo(() => {
        let list = rawOrders;
        const q = (orderSearch || "").toLowerCase().trim();
        if (q) {
            list = list.filter(
                (o) =>
                    formatOrderNumber(o).toLowerCase().includes(q) ||
                    (formatDate(o.date || o.created_at, "") && formatDate(o.date || o.created_at, "").toLowerCase().includes(q)) ||
                    (o.channel && String(o.channel).toLowerCase().includes(q)) ||
                    (o.status && String(o.status).toLowerCase().includes(q))
            );
        }
        if (orderStatusFilter && orderStatusFilter !== "All Statuses") {
            const statusFilterLower = String(orderStatusFilter).toLowerCase().trim();
            list = list.filter((o) => String(o.status || "").toLowerCase().trim() === statusFilterLower);
        }
        return list;
    }, [rawOrders, orderSearch, orderStatusFilter, formatDate]);

    const orderHistoryColumns = useMemo(() => [
        {
            name: "ORDER NUMBER",
            cell: (row) => (
                <Link to={`/order/${row.id}/view`} className="order-history-order-id-link">
                    {formatOrderNumber(row)}
                </Link>
            ),
            sortable: true,
        },
        {
            name: "DATE",
            selector: (row) => row.date || row.order_date || row.created_at,
            cell: (row) => formatDate(row.date || row.order_date || row.created_at, ""),
            sortable: true,
        },
        {
            name: "Partner",
            cell: (row) => (
                <span className="order-history-channel">
                    <i className={`fa ${getChannelIcon(row.channel)} order-history-channel-icon`} />
                    {row.channel || row.source || "-"}
                </span>
            ),
            sortable: true,
        },
        {
            name: "ITEMS",
            selector: (row) => row.items ?? row.item_count ?? "-",
            sortable: true,
        },
        {
            name: "TOTAL",
            selector: (row) => row.total || row.total_price || row.amount,
            cell: (row) => (
                <span className="order-history-total">
                    {formatCurrency(row.total || row.total_price || row.amount)}
                </span>
            ),
            sortable: true,
        },
        {
            name: "STATUS",
            cell: (row) => {
                const variant = getStatusPillVariant(row.status);
                return (
                    <span className={`order-history-status-pill order-history-status-${variant}`}>
                        {variant === "returned" && <i className="fa fa-undo order-history-status-icon" />}
                        {row.status || "-"}
                    </span>
                );
            },
            sortable: true,
        },
    ], [formatDate, formatCurrency]);

    const products = topProducts || [];
    const categories = categoryPreferences || [];
    const cartItems = abandonedCartItems || [];
    const colors = colorPreferences || [];
    const cartSubtotal = abandonedCart?.subtotal ?? abandonedCart?.total ?? null;
    const cartItemCount = cartItems.length;
    const cartCountLabel = `${cartItemCount} ${cartItemCount === 1 ? "Item" : "Items"}`;
    const cartAbandonedLabel = abandonedCart?.abandoned_at
        ? formatDateTime(abandonedCart.abandoned_at)
        : "No abandoned cart data";
    const cartReference = [
        abandonedCart?.cart_token ? `Cart token: ${abandonedCart.cart_token}` : null,
        abandonedCart?.checkout_id ? `Checkout ID: ${abandonedCart.checkout_id}` : null,
    ].filter(Boolean);

    return (
        <>
            <Row>
                <Col sm="12">
                    <Card id="buyer-lead-section-orders" className="buyer-lead-orders-card buyer-lead-section">
                        <div className="buyer-lead-orders-header">
                            <div>
                                <div className="buyer-lead-orders-title">
                                    <i className="fa fa-shopping-bag mr-2"></i> Orders & Financials
                                </div>
                                <div className="buyer-lead-orders-subtitle">
                                    Complete order history, financial metrics and purchasing patterns
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row className="mb-4">
                                <Col lg="4" md="4" sm="6" className="mb-3">
                                    <div className="buyer-lead-metric-card metric-blue">
                                        <div className="buyer-lead-metric-value">{totalOrders ?? "-"}</div>
                                        <div className="buyer-lead-metric-label">Total Orders</div>
                                    </div>
                                </Col>
                                <Col lg="4" md="4" sm="6" className="mb-3">
                                    <div className="buyer-lead-metric-card metric-green">
                                        <div className="buyer-lead-metric-value">{formatCurrency(totalOrderValue)}</div>
                                        <div className="buyer-lead-metric-label">Total Order Value</div>
                                    </div>
                                </Col>
                                <Col lg="4" md="4" sm="6" className="mb-3">
                                    <div className="buyer-lead-metric-card metric-purple">
                                        <div className="buyer-lead-metric-value">{formatCurrency(averageOrderValue)}</div>
                                        <div className="buyer-lead-metric-label">Average Order Value</div>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col lg="4" md="4" sm="6" className="mb-3">
                                    <div className="buyer-lead-stat-card">
                                        <div className="buyer-lead-stat-label">Return Rate</div>
                                        <div className="buyer-lead-stat-value">
                                            {returnRate !== null && returnRate !== undefined ? `${returnRate}%` : "-"}
                                        </div>
                                    </div>
                                </Col>
                                <Col lg="4" md="4" sm="6" className="mb-3">
                                    <div className="buyer-lead-stat-card">
                                        <div className="buyer-lead-stat-label">Refunds Total</div>
                                        <div className="buyer-lead-stat-value">{formatCurrency(refundsTotal)}</div>
                                    </div>
                                </Col>
                                <Col lg="4" md="4" sm="6" className="mb-3">
                                    <div className="buyer-lead-stat-card">
                                        <div className="buyer-lead-stat-label">ROI Per User</div>
                                        <div className="buyer-lead-stat-value">
                                            {roiPerUser !== null && roiPerUser !== undefined ? `${roiPerUser}%` : "-"}
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <div className="buyer-lead-orders-toolbar">
                                <div className="buyer-lead-table-title">Order History</div>
                                <div className="buyer-lead-orders-filters order-history-filters">
                                    <div className="order-history-search-wrap">
                                        <i className="fa fa-search order-history-search-icon" />
                                        <input
                                            type="text"
                                            className="form-control order-history-search-input"
                                            placeholder="Search orders..."
                                            value={orderSearch}
                                            onChange={(e) => {
                                                setOrderSearch(e.target.value);
                                                setOrderPage(1);
                                            }}
                                        />
                                    </div>
                                    <Select
                                        className="order-history-filter-select my-custom-react-select"
                                        classNamePrefix="order-history-select"
                                        value={{ value: orderStatusFilter, label: orderStatusFilter }}
                                        onChange={(opt) => {
                                            setOrderStatusFilter(opt?.value ?? "All Statuses");
                                            setOrderPage(1);
                                        }}
                                        options={orderStatusOptions.map((opt) => ({ value: opt, label: opt }))}
                                        isClearable={false}
                                        placeholder="All Statuses"
                                        styles={orderHistorySelectStyles}
                                    />
                                </div>
                            </div>
                            <div className="buyer-lead-table-wrapper order-history-table-wrapper table-responsive product-table">
                                <CommonDataTable
                                    headerColumns={orderHistoryColumns}
                                    gridData={filteredOrders}
                                    noAction
                                    pagination
                                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                    paginationPerPage={orderPerPage}
                                    paginationDefaultPage={orderPage}
                                    onChangePage={setOrderPage}
                                    onChangeRowsPerPage={(rowsPerPage) => {
                                        setOrderPerPage(rowsPerPage);
                                        setOrderPage(1);
                                    }}
                                    highlightOnHover
                                />
                            </div>

                            <Row className="mt-4">
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-subsection-card">
                                        <div className="buyer-lead-table-title">Top Purchased Products</div>
                                        {products.length === 0 ? (
                                            <div className="text-muted">No product data available.</div>
                                        ) : (
                                            products.map((product, index) => (
                                                <div className="buyer-lead-product-row" key={`product-${index}`}>
                                                    <div>
                                                        <div className="buyer-lead-product-title">
                                                            {product.name || product.title || "-"}
                                                        </div>
                                                        <div className="buyer-lead-product-sub">
                                                            Purchased {product.count ?? product.times ?? "-"} times
                                                        </div>
                                                    </div>
                                                    <div className="buyer-lead-product-value">
                                                        {formatCurrency(product.total || product.value)}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </Col>
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-subsection-card">
                                        <div className="buyer-lead-table-title">Category Preferences</div>
                                        {categories.length === 0 ? (
                                            <div className="text-muted">No category data available.</div>
                                        ) : (
                                            categories.map((category, index) => {
                                                const count = category.count ?? category.items ?? 0;
                                                const total = categories.reduce(
                                                    (sum, entry) => sum + (entry.count ?? entry.items ?? 0),
                                                    0
                                                );
                                                const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                                                return (
                                                    <div className="buyer-lead-category-row" key={`cat-${index}`}>
                                                        <div className="buyer-lead-category-label">
                                                            <span>{category.label || category.name || "-"}</span>
                                                            <span>
                                                                {count} items ({percent}%)
                                                            </span>
                                                        </div>
                                                        <div className="buyer-lead-size-bar-track">
                                                            <div
                                                                className="buyer-lead-size-bar-fill"
                                                                style={{width: `${percent}%`}}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col sm="12">
                    <Card
                        id="buyer-lead-section-abandoned-cart"
                        className="buyer-lead-wishlist-card buyer-lead-section"
                    >
                        <div className="buyer-lead-wishlist-header">
                            <div>
                                <div className="buyer-lead-wishlist-title">
                                    <i className="fa fa-shopping-cart mr-2"></i> Abandoned Cart
                                </div>
                                <div className="buyer-lead-wishlist-subtitle">
                                    Recoverable checkout details and pending cart items
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row className="mb-4">
                                <Col lg="12" className="mb-3">
                                    <div className="buyer-lead-abandoned-card">
                                        <div className="buyer-lead-abandoned-header">
                                            <div className="buyer-lead-abandoned-title">
                                                <i className="fa fa-shopping-cart"></i>
                                                <span>Abandoned Cart</span>
                                            </div>
                                            <div className="buyer-lead-abandoned-actions">
                                                <span className="buyer-lead-abandoned-count">{cartCountLabel}</span>
                                                {abandonedCart?.abandoned_checkout_url && (
                                                    <a
                                                        href={abandonedCart.abandoned_checkout_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="buyer-lead-abandoned-link"
                                                    >
                                                        View checkout
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                        <div className="buyer-lead-abandoned-items">
                                            {cartItems.length === 0 ? (
                                                <div className="buyer-lead-abandoned-empty">
                                                    No abandoned cart items.
                                                </div>
                                            ) : (
                                                cartItems.map((item, index) => {
                                                    const quantity =
                                                        item.quantity ?? item.qty ?? item.count ?? null;
                                                    const sizeLabel = item.size || item.option || "One Size";
                                                    const lineTotal =
                                                        item.total ??
                                                        (item.price !== null &&
                                                        item.price !== undefined &&
                                                        quantity !== null &&
                                                        quantity !== undefined
                                                            ? Number(item.price) * Number(quantity)
                                                            : null);
                                                    const image =
                                                        item.image || item.image_url || item.product_image || null;
                                                    return (
                                                        <div className="buyer-lead-abandoned-item" key={`cart-${index}`}>
                                                            <div className="buyer-lead-abandoned-item-info">
                                                                <div className="buyer-lead-abandoned-thumb">
                                                                    {image ? (
                                                                        <img
                                                                            src={image}
                                                                            alt={item.title || item.name || "Cart item"}
                                                                        />
                                                                    ) : (
                                                                        <div className="buyer-lead-abandoned-thumb-placeholder">
                                                                            <i className="fa fa-image"></i>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="buyer-lead-abandoned-item-text">
                                                                    <div className="buyer-lead-abandoned-item-title">
                                                                        {item.title || item.name || "-"}
                                                                    </div>
                                                                    <div className="buyer-lead-abandoned-item-meta">
                                                                        {[sizeLabel, quantity ? `Qty: ${quantity}` : null]
                                                                            .filter(Boolean)
                                                                            .join(" • ")}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="buyer-lead-abandoned-item-price">
                                                                {formatCurrency(
                                                                    lineTotal || item.price || item.amount
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                        <div className="buyer-lead-abandoned-summary">
                                            <div className="buyer-lead-abandoned-summary-row">
                                                <span>Subtotal:</span>
                                                <strong>{formatCurrency(cartSubtotal)}</strong>
                                            </div>
                                            <div className="buyer-lead-abandoned-summary-row">
                                                <span>Cart abandoned:</span>
                                                <span>{cartAbandonedLabel}</span>
                                            </div>
                                            {cartReference.length > 0 && (
                                                <div className="buyer-lead-abandoned-meta">
                                                    {cartReference.join(" • ")}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn buyer-lead-abandoned-cta"
                                            disabled={cartItems.length === 0}
                                        >
                                            <i className="fa fa-envelope"></i> Send Cart Recovery Email
                                        </button>
                                    </div>
                                </Col>
                            </Row>

                            <div className="buyer-lead-table-title mt-4">Shopping Preferences</div>
                            <Row>
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-preference-card preference-purple">
                                        <div className="buyer-lead-preference-label">Price Range Affinity</div>
                                        <div className="buyer-lead-preference-value">{priceRangePreference}</div>
                                    </div>
                                </Col>
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-preference-card preference-blue">
                                        <div className="buyer-lead-preference-label">Favorite Brand</div>
                                        <div className="buyer-lead-preference-value">{favoriteBrand}</div>
                                    </div>
                                </Col>
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-preference-card preference-green">
                                        <div className="buyer-lead-preference-label">Color Preference</div>
                                        <div className="buyer-lead-preference-value">
                                            {colors.length === 0 ? "-" : colors.join(", ")}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default BuyerLeadCommerce;
